import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { LuArrowDownToLine } from 'react-icons/lu';
import styled from 'styled-components';

import { useProject } from '@contexts/Project.context';
import { directoryExists, fileApiUrl, fetchUrl } from '@utils/api';
import { makePhotoLayer } from '@utils/map/layer/photo';
import { getLayerByCustomId } from '@utils/map/helpers';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';
import { downloadFile } from '@utils/files';
import { useToast } from '@contexts/Toast.context';
import Range from '@components/rangeSlider/Range';

const OpacitySliderContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
	padding-left: 28px;
	position: relative;
`;

const OpacityLabel = styled.label`
	font-size: 0.85rem;
	color: ${props => props.theme.colors.textAccent};
	font-weight: 400;
	min-width: 60px;
`;

const OpacityValue = styled.span`
	font-size: 0.85rem;
	color: ${props => props.theme.colors.textColor};
	min-width: fit-content;
`;

/*
 *  This component is responsible for rendering the photo layer and toggling its visibility.
 */
const PhotoLayer = ({
	name,
	layerId,
	directory,
	zIndex,
	visible = true,
	checkDirectory = false,
	downloadPath = null,
	showOpacityControl = false,
	tilesPath = null,
	minZoomLevel = null,
	maxZoomLevel = null,
	layerExtent = null,
}) => {
	const { addToast } = useToast();
	const { mapObject, defaultProjection, project, extent } = useProject();

	const adding = useRef(false);
	const [photoLayer, setPhotoLayer] = useState(null);
	const [opacity, setOpacity] = useState(1);
	const [isVisible, setIsVisible] = useState(visible);
	const { title, uuid } = project;

	const projectDirectory = tilesPath ?? uuid + '/' + directory;

	const { data: checkDirectoryExists } = useQuery({
		queryKey: ['directoryExists', projectDirectory],
		queryFn: () => directoryExists(projectDirectory),
		enabled: !!projectDirectory && checkDirectory,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchOnMount: false,
		keepPreviousData: true,
	});

	const { mutateAsync: getSignedImage } = useMutation({
		// mutationFn: () => fetchUrl(`fileLink?key=${uuid}/${downloadPath}`),
		mutationFn: () => fetchUrl(`fileLink?key=${downloadPath}`),
	});

	const download = async () => {
		try {
			const data = await getSignedImage();

			const imageUrl = data?.url;

			if (!imageUrl) {
				throw new Error('No image URL found');
			}
			const imageData = {
				url: imageUrl,
				title: `${title}_orthophoto`,
				fileExtension: 'tif',
			};

			downloadFile(imageData);
		} catch (error) {
			console.warn('Error downloading image:', error);
			addToast({
				id: 'image-download-error',
				bg: 'danger',
				title: 'Image could not be downloaded. Please try again later.',
			});
		}
	};

	const directoryDoesExists = !checkDirectory ? true : checkDirectoryExists;

	const updatePhotoLayer = useCallback(() => {
		if (!mapObject || !extent || adding.current || !directoryDoesExists) return null;

		adding.current = true;

		// Check if the layer already has been added to the map. It could be if this component is re-rendered.
		const existingLayer = getLayerByCustomId(mapObject, layerId);
		if (existingLayer) {
			console.log(`Layer ${name} already exists. Adding it to state.`);
			setPhotoLayer(existingLayer);
			adding.current = false;

			return;
		}

		const xyzUrl = fileApiUrl + '/filelink?key=' + projectDirectory + '/{z}/{x}/{-y}.png';

		const layerMinZoom = minZoomLevel ?? project?.min_zoom_level ?? 10;
		const layerMaxZoom = maxZoomLevel ?? project?.max_zoom_level ?? 21;

		const newLayer = makePhotoLayer({
			name,
			zIndex,
			defaultProjection,
			url: xyzUrl,
			extent: layerExtent || extent,
			minZoomLevel: layerMinZoom,
			maxZoomLevel: layerMaxZoom,
			visible,
			layerId,
		});

		if (newLayer) mapObject.addLayer(newLayer);
		setPhotoLayer(newLayer);

		adding.current = false;
	}, [mapObject, directoryDoesExists]);

	useEffect(() => {
		updatePhotoLayer();
	}, [updatePhotoLayer]);

	// Handle opacity changes
	useEffect(() => {
		if (photoLayer) {
			photoLayer.setOpacity(opacity);
		}
	}, [photoLayer, opacity]);

	if (!photoLayer) return null;
	const layerFunctions = downloadPath
		? [
				{
					title: 'Download Photo',
					onClick: download,
					icon: <LuArrowDownToLine />,
				},
			]
		: null;

	const handleCheck = () => {
		photoLayer?.setVisible(true);
		setIsVisible(true);
	};

	const handleUncheck = () => {
		photoLayer?.setVisible(false);
		setIsVisible(false);
	};

	return (
		<li>
			<LayerSettings
				id={layerId}
				layer={photoLayer}
				label={photoLayer.get('name')}
				defaultState={photoLayer.getVisible()}
				onCheck={handleCheck}
				onUncheck={handleUncheck}
				disabled={!photoLayer}
				layerFunctions={layerFunctions}
			/>
			{showOpacityControl && isVisible && (
				<OpacitySliderContainer>
					<OpacityLabel htmlFor={`${layerId}-opacity`}>Opacity:</OpacityLabel>
					<Range
						id={`${layerId}-opacity`}
						min={0}
						max={1}
						step={0.01}
						values={[opacity]}
						onChange={([num]) => setOpacity(num)}
						onFinalChange={([num]) => setOpacity(num)}
						trackColors={['#20AC6B', '#343a40']}
						radiusConfig={{ start: 0, end: 0.06, radiusStart: 0, radiusEnd: 6 }}
						hideMinMaxLabels
						hideThumbLabels
					/>
					<OpacityValue>{Math.round(opacity * 100)}%</OpacityValue>
				</OpacitySliderContainer>
			)}
		</li>
	);
};

export default PhotoLayer;
